<template>
  <button
    class="vz-bt"
    :aria-label="ariaLabel"
    :class="{ 'vz-loading': loading }"
    :type="type"
    :disabled="disabled ? 'disabled' : false"
    @click="dispatchClick"
  >
    <span class="vz-bt-label">
      <slot />
    </span>
    <transition>
      <span class="vz-bt-loading vz-spin" v-if="loading">
        <i class="ic-loader vz-bt-loading-ic"></i>
      </span>
    </transition>
  </button>
</template>

<script>
import Base from "../vz-base.vue";

/**
 * Button.
 * @module VzButton
 * @param {String} label Button label
 * @param {String} type Value for html _type_ attribute
 * @param {String} disabled Whether button should be disabled
 * @param {String} iconClass Css classname of icon to show next to label
 * @param {Boolean} loading Whether to show loading animation
 * @emits {Event} Click event
 * @author Lanza
 */
export default {
  name: "VzButton",
  extends: Base,
  emits: ["click"],

  props: {
    type: {
      type: String,
      default: "button",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: null,
    },
  },

  methods: {
    dispatchClick: function () {
      this.$emit("click");
    },
  },
};
</script>

<style>
.vz-bt,
.vz-bt-label,
.vz-bt-icon {
  display: inline-block;
}

.vz-bt {
  position: relative;
  line-height: 1em;
  font-size: 0.875em;
  padding: 16px 32px;
  cursor: pointer;
  box-shadow: 0 1px 16px rgba(0, 0, 0, 0.85);
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.4);
  border-width: 1px;
  border-style: solid;
  border-color: var(--primary-color);
  color: var(--primary-color);
  outline: none;
  transition-property: background-color, color;
  transition-duration: 0.3s;
}

.vz-bt:hover {
  background-color: var(--primary-color-over);
  color: var(--primary-color-over-contrast);
}

.vz-bt:active {
  background-color: var(--primary-color-active);
  color: var(--primary-color-active-contrast);
  box-shadow: none;
}

.vz-primary {
  background-color: var(--primary-color);
  color: var(--primary-color-contrast);
}

.vz-secondary {
  background-color: var(--secondary-color);
  color: var(--secondary-color-contrast);
  border-color: var(--secondary-color);
}

.vz-secondary:hover {
  background-color: var(--secondary-color-over);
  color: var(--secondary-color-over-contrast);
  border-color: var(--secondary-color-over);
}

.vz-secondary:active {
  background-color: var(--secondary-color-active);
  color: var(--secondary-color-active-contrast);
  border-color: var(--secondary-color-active);
}

.vz-loading,
.vz-bt[disabled] {
  cursor: default;
  box-shadow: none;
}

.vz-bt[disabled] {
  opacity: 0.5;
  box-shadow: none;
}

.vz-bt-label {
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.8);
  text-transform: uppercase;
  opacity: 1;
  transition-property: opacity;
  transition-duration: 0.3s;
  transition-delay: 0.3s;
  position: relative;
}

.vz-loading .vz-bt-label {
  transition-delay: 0s;
  opacity: 0;
}

.vz-bt-icon {
  display: inline-block;
  margin-left: 8px;
  opacity: 0.6;
  font-size: 1.2em;
  vertical-align: middle;
  height: 1px;
}

.vz-bt-icon:before {
  display: inline-block;
  transform: translate(0, -50%);
}

.vz-bt-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  margin: -9px 0 0 -9px;
  font-size: 18px;
}

.vz-bt-loading.v-enter-active,
.vz-bt-loading.v-leave-active {
  transition-property: opacity;
  transition-duration: 0.3s;
}

.vz-bt-loading.v-enter-active {
  transition-delay: 0.3s;
}

.vz-bt-loading.v-leave-active {
  transition-delay: 0s;
}

.vz-bt-loading.v-enter-from,
.vz-bt-loading.v-enter,
.vz-bt-loading.v-leave-to {
  opacity: 0;
}

.vz-bt-loading.v-leave-from,
.vz-bt-loading.v-leave,
.vz-bt-loading.v-enter-to {
  opacity: 1;
}
</style>
